import React from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Printer } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';

const AGB = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();

  const handleClose = () => {
    // Geri git ve scroll to top sinyali gönder
    navigate(-1, { state: { scrollToTop: true } });
    // Ekstra güvenlik - biraz bekle ve scroll yap
    setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }, 50);
  };

  const handlePrint = () => {
    const printContent = document.getElementById('agb-content');
    const title = language === 'de' ? 'Allgemeine Geschäftsbedingungen (AGB)' : 'General Terms and Conditions (GTC)';
    
    // Gizli iframe oluştur
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.top = '-10000px';
    iframe.style.left = '-10000px';
    document.body.appendChild(iframe);
    
    const iframeDoc = iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>SLÁI Events - ${title}</title>
          <style>
            @page { margin: 20mm; }
            body { font-family: Arial, sans-serif; padding: 10px; line-height: 1.5; font-size: 11pt; color: #333; }
            h1 { font-size: 22pt; margin-bottom: 8px; }
            h2 { font-size: 13pt; margin-top: 16px; margin-bottom: 8px; font-weight: bold; color: #333; }
            p { margin-bottom: 8px; color: #555; }
            ul { margin-left: 20px; margin-bottom: 8px; }
            li { margin-bottom: 4px; color: #555; }
            .provider { margin-bottom: 16px; padding-bottom: 12px; border-bottom: 1px solid #ccc; }
            a { color: #d97706; }
          </style>
        </head>
        <body>${printContent.innerHTML}</body>
      </html>
    `);
    iframeDoc.close();
    
    // Print ve iframe temizle
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
    
    // Print dialog kapandıktan sonra iframe'i kaldır
    setTimeout(() => {
      document.body.removeChild(iframe);
    }, 1000);
  };

  return (
    <div className="min-h-screen pt-16 bg-[#1a1a1a]">
      {/* Header */}
      <section className="py-12 sm:py-20 bg-[#1a1a1a] border-b border-white/10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center relative">
            <button
              onClick={handleClose}
              className="absolute top-0 right-0 p-2 text-white/60 hover:text-[#8b7355] transition-colors"
              aria-label="Close"
            >
              <X className="w-8 h-8" />
            </button>
            <p className="text-xs tracking-[0.3em] uppercase text-[#8b7355] mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {language === 'de' ? 'RECHTLICHES' : 'LEGAL'}
            </p>
            <h1 className="text-4xl sm:text-5xl md:text-6xl text-white mb-4 font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {language === 'de' ? 'Allgemeine Geschäftsbedingungen (AGB)' : 'Terms & Conditions'}
            </h1>
            <div className="w-16 h-[1px] bg-white/40 mx-auto mb-4" />
            <p className="text-sm text-white/60" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
              {language === 'de' ? 'für Dienstleistungen im Bereich Event- und Cateringservices' : 'for Event and Catering Services'}
            </p>
          </div>
        </div>
      </section>

      {/* AGB Content */}
      <section className="py-12 sm:py-16 bg-[#1a1a1a]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto bg-[#2a2a2a] border border-[#e67e22] rounded-lg p-8 sm:p-12">
            
            <div id="agb-content" className="space-y-8 text-white/70">
              {/* Provider Info */}
              <div className="provider border-b border-white/10 pb-8">
                <h2 className="text-xl text-[#8b7355] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>{language === 'de' ? 'Anbieter:' : 'Provider:'}</h2>
                <p className="leading-relaxed" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
                  SLÁI Events<br />
                  {language === 'de' ? 'Inhaber' : 'Owner'}: Cem Koptas<br />
                  Bärensteinstraße 2<br />
                  12685 Berlin<br />
                  {language === 'de' ? 'E-Mail' : 'Email'}: <a href="mailto:info@slaievents.com" className="text-[#8b7355] hover:underline">info@slaievents.com</a>
                </p>
              </div>

              {language === 'de' ? (
                <>
                  {/* German Content */}
                  <div>
                    <h2 className="text-xl text-white mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>§ 1 Geltungsbereich</h2>
                    <p className="leading-relaxed text-white/60" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>Diese Allgemeinen Geschäftsbedingungen (AGB) gelten für alle Verträge, Angebote, Lieferungen und Leistungen zwischen SLÁI Events (nachfolgend „Auftragnehmer") und seinen Kunden (nachfolgend „Auftraggeber"). Abweichende Bedingungen des Auftraggebers finden keine Anwendung, es sei denn, ihrer Geltung wird ausdrücklich und schriftlich zugestimmt.</p>
                  </div>

                  <div>
                    <h2 className="text-xl text-white mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>§ 2 Vertragsgegenstand</h2>
                    <p className="leading-relaxed text-white/60" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>Gegenstand des Vertrages sind Dienstleistungen im Bereich Eventorganisation, Eventmanagement, Catering, mobiler Verkauf von Speisen und Getränken sowie damit zusammenhängende Leistungen. Der konkrete Leistungsumfang ergibt sich aus dem jeweiligen Angebot oder der schriftlichen Vereinbarung.</p>
                  </div>

                  <div>
                    <h2 className="text-xl font-bold text-white mb-3">§ 3 Vertragsschluss</h2>
                    <p className="leading-relaxed text-white/60">Ein Vertrag kommt zustande, sobald der Auftraggeber ein Angebot des Auftragnehmers schriftlich bestätigt (z. B. per E-Mail oder Unterschrift).</p>
                  </div>

                  <div>
                    <h2 className="text-xl font-bold text-white mb-3">§ 4 Preise und Zahlungsbedingungen</h2>
                    <p className="leading-relaxed text-white/60">Alle Preise verstehen sich in Euro zuzüglich der gesetzlichen Umsatzsteuer, sofern nicht ausdrücklich anders angegeben.</p>
                  </div>

                  <div>
                    <h2 className="text-xl font-bold text-white mb-3">§ 5 Anzahlungen und Zahlungsfälligkeit</h2>
                    <p className="leading-relaxed text-white/60">Der Auftragnehmer ist berechtigt, bei Vertragsschluss eine angemessene Anzahlung zu verlangen. Die vollständige Zahlung ist Voraussetzung für die Durchführung der Veranstaltung. Die Durchführung der Veranstaltung setzt voraus, dass der vollständige Rechnungsbetrag vor Veranstaltungsbeginn auf dem Konto des Auftragnehmers eingegangen ist.</p>
                  </div>

                  <div>
                    <h2 className="text-xl font-bold text-white mb-3">§ 6 Änderungen des Leistungsumfangs</h2>
                    <p className="leading-relaxed text-white/60">Änderungen der Teilnehmerzahl oder des vereinbarten Leistungsumfangs sind bis spätestens 5 Kalendertage vor Veranstaltungsbeginn kostenfrei im Rahmen einer Toleranz von ±10% möglich. Weitergehende Änderungen oder kurzfristige Anpassungen können zu Mehrkosten führen.</p>
                  </div>

                  <div>
                    <h2 className="text-xl font-bold text-white mb-3">§ 7 Stornierung und Rücktritt durch den Auftraggeber</h2>
                    <p className="leading-relaxed text-white/60 mb-3">Es gelten folgende verbindliche Stornierungspauschalen:</p>
                    <ul className="list-disc list-inside text-white/60 space-y-1 ml-4">
                      <li>Bis 30 Kalendertage vor Veranstaltungsbeginn: 20%</li>
                      <li>Bis 14 Kalendertage vor Veranstaltungsbeginn: 50%</li>
                      <li>Ab 7 Kalendertage vor Veranstaltungsbeginn oder Nichterscheinen: 100%</li>
                    </ul>
                    <p className="leading-relaxed text-white/60 mt-3">Unabhängig vom Zeitpunkt der Stornierung sind bereits entstandene oder verbindlich beauftragte Kosten für Fremdleistungen (z. B. Technik, Künstler, Mietmaterialien, Waren) vom Auftraggeber zu 100% zu erstatten. Dies gilt auch dann, wenn der Rücktritt mehr als 30 Kalendertage vor Veranstaltungsbeginn erfolgt.</p>
                  </div>

                  <div>
                    <h2 className="text-xl font-bold text-white mb-3">§ 8 Mitwirkungspflichten, Genehmigungen und GEMA</h2>
                    <p className="leading-relaxed text-white/60">Der Auftraggeber ist verpflichtet, alle für die Durchführung der Veranstaltung erforderlichen Informationen rechtzeitig bereitzustellen. Der Auftraggeber trägt die alleinige Verantwortung für behördliche Genehmigungen sowie für die Anmeldung bei der GEMA und die Zahlung aller anfallenden Gebühren.</p>
                  </div>

                  <div>
                    <h2 className="text-xl font-bold text-white mb-3">§ 9 Versicherungspflicht des Auftraggebers</h2>
                    <p className="leading-relaxed text-white/60">Der Auftraggeber verpflichtet sich, für die Dauer der Veranstaltung eine angemessene Haftpflichtversicherung abzuschließen, welche Personen-, Sach- und Vermögensschäden abdeckt. Der Auftragnehmer ist berechtigt, einen Versicherungsnachweis anzufordern.</p>
                  </div>

                  <div>
                    <h2 className="text-xl font-bold text-white mb-3">§ 10 Catering, Reinigung und Abfall</h2>
                    <p className="leading-relaxed text-white/60">Der Auftragnehmer übernimmt eine übliche Endreinigung im Rahmen der vereinbarten Leistungen. Zusätzliche Reinigungsarbeiten, die über den üblichen Reinigungsaufwand hinausgehen, wie z. B. durch verschüttete Getränke, übermäßigen Einsatz von Konfetti oder vergleichbare Umstände, können gesondert in Rechnung gestellt werden. Der Auftraggeber ist verpflichtet, bekannte Allergien oder Unverträglichkeiten der Gäste rechtzeitig vor der Veranstaltung schriftlich mitzuteilen.</p>
                  </div>

                  <div>
                    <h2 className="text-xl font-bold text-white mb-3">§ 11 Equipment, Rückgabe und Schäden</h2>
                    <p className="leading-relaxed text-white/60">Bereitgestelltes Equipment ist unmittelbar nach Veranstaltungsende vollständig und unbeschädigt zu übergeben. Bei Verlust, Beschädigung oder fehlender Rückgabe erfolgt eine Abrechnung zum Wiederbeschaffungswert eines gleichwertigen neuen Produkts.</p>
                  </div>

                  <div>
                    <h2 className="text-xl font-bold text-white mb-3">§ 12 Haftung und Lebensmittelsicherheit</h2>
                    <p className="leading-relaxed text-white/60">Bei Catering-Leistungen geht die Verantwortung für die sachgemäße Lagerung, Kühlung und den Verzehr der Speisen spätestens zwei Stunden nach Bereitstellung auf den Auftraggeber über. Der Auftragnehmer haftet nicht für gesundheitliche Beeinträchtigungen, die auf eine unsachgemäße Lagerung oder den verspäteten Verzehr zurückzuführen sind.</p>
                  </div>

                  <div>
                    <h2 className="text-xl font-bold text-white mb-3">§ 13 Datenschutz sowie Bild- und Videoaufnahmen</h2>
                    <p className="leading-relaxed text-white/60">Die Verarbeitung personenbezogener Daten sowie die Nutzung von Foto- und Videoaufnahmen erfolgt im Einklang mit der Datenschutz-Grundverordnung (DSGVO).</p>
                  </div>

                  <div>
                    <h2 className="text-xl font-bold text-white mb-3">§ 14 Vertragsübertragung</h2>
                    <p className="leading-relaxed text-white/60">Eine Übertragung der Rechte und Pflichten aus dem Vertragsverhältnis auf Dritte ist nur mit vorheriger schriftlicher Zustimmung des Auftragnehmers zulässig. Der ursprüngliche Auftraggeber bleibt für sämtliche Verpflichtungen gesamtschuldnerisch haftbar.</p>
                  </div>

                  <div>
                    <h2 className="text-xl font-bold text-white mb-3">§ 15 Alkoholausschank und Jugendschutz</h2>
                    <p className="leading-relaxed text-white/60">Die gesetzlichen Bestimmungen des Jugendschutzgesetzes (JuSchG) sind strikt einzuhalten. Die Abgabe und der Konsum von alkoholischen Getränken an Personen unter 18 Jahren ist untersagt. Der Auftraggeber ist dafür verantwortlich, geeignete Maßnahmen zur Einhaltung der Jugendschutzbestimmungen zu treffen. Der Auftragnehmer übernimmt keine Haftung für Verstöße, die durch Gäste oder Dritte verursacht werden.</p>
                  </div>

                  <div>
                    <h2 className="text-xl font-bold text-white mb-3">§ 16 Schlussbestimmungen</h2>
                    <p className="leading-relaxed text-white/60">Es gilt das Recht der Bundesrepublik Deutschland. Gerichtsstand ist Berlin, soweit gesetzlich zulässig. Sollte eine Bestimmung dieser AGB unwirksam sein, bleibt die Wirksamkeit der übrigen Bestimmungen unberührt.</p>
                  </div>
                </>
              ) : (
                <>
                  {/* English Content - Exact from PDF */}
                  <div>
                    <h2 className="text-xl font-bold text-white mb-3">§ 1 Scope of Application</h2>
                    <p className="leading-relaxed text-white/60">These General Terms and Conditions (GTC) apply to all contracts, offers, deliveries and services between SLÁI Events (hereinafter referred to as the "Contractor") and its customers (hereinafter referred to as the "Client"). Deviating terms and conditions of the Client shall not apply unless their validity has been expressly agreed to in writing.</p>
                  </div>

                  <div>
                    <h2 className="text-xl font-bold text-white mb-3">§ 2 Subject Matter of the Contract</h2>
                    <p className="leading-relaxed text-white/60">The subject matter of the contract is the provision of services in the field of event organization, event management, catering, mobile sale of food and beverages, as well as related services. The specific scope of services results from the respective offer or written agreement.</p>
                  </div>

                  <div>
                    <h2 className="text-xl font-bold text-white mb-3">§ 3 Conclusion of Contract</h2>
                    <p className="leading-relaxed text-white/60">A contract is concluded as soon as the Client confirms an offer of the Contractor in writing (e.g. by email or signature).</p>
                  </div>

                  <div>
                    <h2 className="text-xl font-bold text-white mb-3">§ 4 Prices and Payment Terms</h2>
                    <p className="leading-relaxed text-white/60">All prices are stated in euros plus statutory value-added tax, unless expressly stated otherwise.</p>
                  </div>

                  <div>
                    <h2 className="text-xl font-bold text-white mb-3">§ 5 Advance Payments and Payment Due Date</h2>
                    <p className="leading-relaxed text-white/60">The Contractor is entitled to demand a reasonable advance payment upon conclusion of the contract. Full payment is a prerequisite for the performance of the event. The performance of the event requires that the full invoice amount has been received in the Contractor's bank account prior to the start of the event.</p>
                  </div>

                  <div>
                    <h2 className="text-xl font-bold text-white mb-3">§ 6 Changes to the Scope of Services</h2>
                    <p className="leading-relaxed text-white/60">Changes to the number of participants or the agreed scope of services are possible free of charge up to 5 calendar days before the start of the event within a tolerance of ±10%. Further changes or short-notice adjustments may result in additional costs.</p>
                  </div>

                  <div>
                    <h2 className="text-xl font-bold text-white mb-3">§ 7 Cancellation and Withdrawal by the Client</h2>
                    <p className="leading-relaxed text-white/60 mb-3">The following binding cancellation fees apply:</p>
                    <ul className="list-disc list-inside text-white/60 space-y-1 ml-4">
                      <li>Up to 30 calendar days before the start of the event: 20%</li>
                      <li>Up to 14 calendar days before the start of the event: 50%</li>
                      <li>From 7 calendar days before the start of the event or in case of no-show: 100%</li>
                    </ul>
                    <p className="leading-relaxed text-white/60 mt-3">Regardless of the time of cancellation, costs already incurred or bindingly commissioned for third-party services (e.g. technology, artists, rental materials, goods) must be reimbursed by the Client at 100%. This also applies if the withdrawal occurs more than 30 calendar days before the start of the event.</p>
                  </div>

                  <div>
                    <h2 className="text-xl font-bold text-white mb-3">§ 8 Cooperation Obligations, Permits and GEMA</h2>
                    <p className="leading-relaxed text-white/60">The Client is obliged to provide all information required for the execution of the event in due time. The Client bears sole responsibility for official permits as well as for registration with GEMA and payment of all applicable fees.</p>
                  </div>

                  <div>
                    <h2 className="text-xl font-bold text-white mb-3">§ 9 Insurance Obligation of the Client</h2>
                    <p className="leading-relaxed text-white/60">The Client undertakes to take out appropriate liability insurance for the duration of the event, covering personal injury, property damage and financial loss. The Contractor is entitled to request proof of insurance.</p>
                  </div>

                  <div>
                    <h2 className="text-xl font-bold text-white mb-3">§ 10 Catering, Cleaning and Waste</h2>
                    <p className="leading-relaxed text-white/60">The Contractor provides standard final cleaning within the scope of the agreed services. Additional cleaning work exceeding the usual level, such as caused by spilled beverages, excessive use of confetti or comparable circumstances, may be charged to the Client separately. The Client is obliged to notify the Contractor in writing of any known allergies or intolerances of guests prior to the event.</p>
                  </div>

                  <div>
                    <h2 className="text-xl font-bold text-white mb-3">§ 11 Equipment, Return and Damages</h2>
                    <p className="leading-relaxed text-white/60">Provided equipment must be returned in full and undamaged immediately after the end of the event. In the event of loss, damage or non-return, settlement shall be made at the replacement value of an equivalent new product.</p>
                  </div>

                  <div>
                    <h2 className="text-xl font-bold text-white mb-3">§ 12 Liability and Food Safety</h2>
                    <p className="leading-relaxed text-white/60">In the case of catering services, responsibility for proper storage, cooling and consumption of the food passes to the Client no later than two hours after provision. The Contractor shall not be liable for health impairments resulting from improper storage or delayed consumption.</p>
                  </div>

                  <div>
                    <h2 className="text-xl font-bold text-white mb-3">§ 13 Data Protection and Image and Video Recordings</h2>
                    <p className="leading-relaxed text-white/60">The processing of personal data and the use of photo and video recordings are carried out in accordance with the General Data Protection Regulation (GDPR).</p>
                  </div>

                  <div>
                    <h2 className="text-xl font-bold text-white mb-3">§ 14 Assignment of Contract</h2>
                    <p className="leading-relaxed text-white/60">Transfer of rights and obligations arising from the contractual relationship to third parties is only permitted with the prior written consent of the Contractor. The original Client remains jointly and severally liable for all obligations.</p>
                  </div>

                  <div>
                    <h2 className="text-xl font-bold text-white mb-3">§ 15 Alcohol Service and Youth Protection</h2>
                    <p className="leading-relaxed text-white/60">The statutory provisions of the Youth Protection Act (JuSchG) must be strictly observed. The distribution and consumption of alcoholic beverages to persons under the age of 18 is prohibited. The Client is responsible for taking appropriate measures to ensure compliance with youth protection regulations. The Contractor assumes no liability for violations caused by guests or third parties.</p>
                  </div>

                  <div>
                    <h2 className="text-xl font-bold text-white mb-3">§ 16 Final Provisions</h2>
                    <p className="leading-relaxed text-white/60">The law of the Federal Republic of Germany shall apply. Place of jurisdiction is Berlin, insofar as legally permissible. Should any provision of these GTC be or become invalid, the validity of the remaining provisions shall remain unaffected.</p>
                  </div>
                </>
              )}
            </div>

            {/* Action Buttons - content dışında, yazdırılmayacak */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-12 pt-8 border-t border-white/20">
              <Button
                onClick={handlePrint}
                className="bg-[#3a3a3a] text-white border border-[#e67e22] hover:bg-[#4a4a4a] rounded-none uppercase tracking-[0.15em] text-xs px-8 py-3"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                <Printer className="w-4 h-4 mr-2" />
                {language === 'de' ? 'DRUCKEN' : 'PRINT'}
              </Button>
              <Button
                onClick={handleClose}
                className="bg-[#3a3a3a] text-white border border-[#e67e22] hover:bg-[#4a4a4a] rounded-none uppercase tracking-[0.15em] text-xs px-8 py-3"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                <X className="w-4 h-4 mr-2" />
                {language === 'de' ? 'SCHLIESSEN' : 'CLOSE'}
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AGB;
